import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Lock, Eye, EyeOff, Phone, MapPin, Users, Home, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

import { API } from "../config/api";

const PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", 
  "Newfoundland and Labrador", "Nova Scotia", "Ontario", 
  "Prince Edward Island", "Quebec", "Saskatchewan",
  "Northwest Territories", "Nunavut", "Yukon"
];

const COUNTRIES = ["Canada", "United States", "India", "Other"];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    gender: "male",
    streetAddress: "",
    city: "",
    postalCode: "",
    province: "",
    country: "Canada",
    spouseName: "",
    childrenNames: "",
    nativePlace: "",
    otherDependents: "",
    agreePrivacy: false,
    membershipPlan: "Family"
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === "checkbox" ? checked : value 
    });
  };

  const getPasswordStrength = (password) => {
    if (!password) return { label: "", color: "" };
    if (password.length < 6) return { label: "Weak", color: "text-red-500" };
    if (password.length < 8) return { label: "Medium", color: "text-yellow-500" };
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return { label: "Strong", color: "text-green-500" };
    }
    return { label: "Medium", color: "text-yellow-500" };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreePrivacy) {
      toast.error("Please agree to the Privacy Policy");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/member/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.username,
          name: `${formData.firstName} ${formData.lastName}`,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          password: formData.password,
          gender: formData.gender,
          address: {
            street: formData.streetAddress,
            city: formData.city,
            postal_code: formData.postalCode,
            province: formData.province,
            country: formData.country
          },
          family: {
            spouse_name: formData.spouseName,
            children_names: formData.childrenNames,
            native_place: formData.nativePlace,
            other_dependents: formData.otherDependents
          },
          membership_plan: formData.membershipPlan
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("member_token", data.access_token);
        localStorage.setItem("member_data", JSON.stringify(data.member));
        toast.success("Registration successful! Welcome to Tatvamasi.");
        navigate("/member");
      } else {
        toast.error(data.detail || "Registration failed");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-kerala-cream" data-testid="register-page">
      {/* Form Section */}
      <section className="section-padding">
        <div className="section-container max-w-2xl">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="font-heading text-3xl md:text-4xl text-kerala-green text-center mb-8">
              Tatvamasi Membership Registration
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username */}
              <div>
                <Label htmlFor="username">Username *</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleChange}
                    className="pl-10 bg-blue-50 border-blue-100 focus:bg-white"
                    data-testid="register-username-input"
                  />
                </div>
              </div>

              {/* First Name & Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="mt-2"
                    data-testid="register-firstname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="mt-2"
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 bg-gray-50"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {formData.password && (
                  <p className={`text-sm mt-1 text-right ${passwordStrength.color}`}>
                    Strength: {passwordStrength.label}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <Label>Gender</Label>
                <div className="flex gap-6 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === "male"}
                      onChange={handleChange}
                      className="w-4 h-4 text-kerala-gold border-kerala-gold focus:ring-kerala-gold"
                    />
                    <span>Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === "female"}
                      onChange={handleChange}
                      className="w-4 h-4 text-kerala-gold border-kerala-gold focus:ring-kerala-gold"
                    />
                    <span>Female</span>
                  </label>
                </div>
              </div>

              {/* Divider */}
              <hr className="border-kerala-gold/20" />

              {/* Address Section */}
              <h2 className="font-heading text-2xl text-kerala-green">Address</h2>

              {/* Street Address */}
              <div>
                <Label htmlFor="streetAddress">Street Address</Label>
                <Input
                  id="streetAddress"
                  name="streetAddress"
                  type="text"
                  placeholder="Street Address"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              {/* City & Postal Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    placeholder="Postal Code"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Province & Country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="province">Province/State</Label>
                  <div className="relative mt-2">
                    <select
                      id="province"
                      name="province"
                      value={formData.province}
                      onChange={handleChange}
                      className="w-full h-10 px-3 pr-10 bg-kerala-cream/50 border border-input rounded-md text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-kerala-green"
                    >
                      <option value="">Select Option</option>
                      {PROVINCES.map(prov => (
                        <option key={prov} value={prov}>{prov}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <div className="relative mt-2">
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full h-10 px-3 pr-10 bg-kerala-cream/50 border border-input rounded-md text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-kerala-green"
                    >
                      {COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <hr className="border-kerala-gold/20" />

              {/* Family Details Section */}
              <h2 className="font-heading text-2xl text-kerala-green">Family Details</h2>

              {/* Spouse's Name */}
              <div>
                <Label htmlFor="spouseName">Spouse's Name</Label>
                <Input
                  id="spouseName"
                  name="spouseName"
                  type="text"
                  placeholder="Spouse's Name"
                  value={formData.spouseName}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              {/* Children's Names */}
              <div>
                <Label htmlFor="childrenNames">Children's Name(s)</Label>
                <Input
                  id="childrenNames"
                  name="childrenNames"
                  type="text"
                  placeholder="Children's Name (separate multiple names with comma)"
                  value={formData.childrenNames}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              {/* Native Place */}
              <div>
                <Label htmlFor="nativePlace">Native Place in Kerala/India</Label>
                <Input
                  id="nativePlace"
                  name="nativePlace"
                  type="text"
                  placeholder="Native Place in Kerala/India"
                  value={formData.nativePlace}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              {/* Other Dependents */}
              <div>
                <Label htmlFor="otherDependents">Other Dependent's Name</Label>
                <Input
                  id="otherDependents"
                  name="otherDependents"
                  type="text"
                  placeholder="Other Dependent's Name"
                  value={formData.otherDependents}
                  onChange={handleChange}
                  className="mt-2"
                />
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreePrivacy"
                  name="agreePrivacy"
                  checked={formData.agreePrivacy}
                  onChange={handleChange}
                  className="w-5 h-5 mt-0.5 border-gray-300 rounded text-kerala-green focus:ring-kerala-green"
                />
                <label htmlFor="agreePrivacy" className="text-sm text-gray-600">
                  I have read, understood and agree to the{" "}
                  <Link to="/privacy" className="text-kerala-green underline hover:text-kerala-terracotta">
                    Privacy Policy
                  </Link>.
                </label>
              </div>

              {/* Selected Plan */}
              <div className="bg-kerala-cream/50 p-4 rounded-lg text-center">
                <p className="text-gray-600">
                  Your currently selected plan: <span className="font-semibold text-kerala-green">Family</span>
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-14 text-lg font-semibold text-white rounded-full"
                style={{
                  background: "linear-gradient(135deg, #B8860B 0%, #CD853F 50%, #D2691E 100%)"
                }}
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? "Registering..." : "Register"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-kerala-green hover:text-kerala-terracotta font-medium">
                  Login here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
